import React, { useMemo, useState } from "react";
import { useQuery, useApolloClient, gql } from "@apollo/client";
import { take, jobsSettings } from "@/root/config";
import JobList from "./JobList";
import Button from "@/common/UI/Button";
import Loader from "@/common/UI/Animated/Loader";
import appText from "@/lang/appText";
import styled from "styled-components";
import Translator from "@/components/hoc/Translator";

const StyledJobsCards = styled.div`
  .BottomMessage {
    text-align: center;
    font-weight: bold;
    font-size: 1.2em;
  }

  padding-bottom: 30px;
`;

const SEARCH_JOBS_QUERY = gql`
  query SEARCH_JOBS_QUERY(
    $query: String
    $location: String
    $take: Int!
    $skip: Int!
    $radius: Int
    $where: JobWhereInput
  ) {
    searchJobs(
      query: $query
      location: $location
      where: $where
      take: $take
      skip: $skip
      radius: $radius
    ) {
      id
      title
      description
      minCompensation
      maxCompensation
      favorites {
        id
      }
      type
      createdAt
      updatedAt
      location {
        id
        name
      }

      perks(where: { status: ACTIVE }) {
        id
        name
      }
    }
  }
`;

function searchWhere(category, type) {
  const where = {};
  if (category) {
    where.categories = { some: { name: { contains: category } } };
  }
  if (type) {
    where.type = { contains: type };
  }
  return where;
}

const JobsCards = (props) => {
  const client = useApolloClient();
  const variables = useMemo(
    () => ({
      location: props.location || "",
      query: props.q || "",
      radius: props.radius || 5,
      take,
      skip: 0,
      where: searchWhere(props.category, props.type),
    }),
    [props.location, props.q, props.radius, props.category, props.type]
  );
  const hasSeedJobs = Array.isArray(props.jobs);
  const [appended, setAppended] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreExhausted, setMoreExhausted] = useState(false);

  const { error, loading, data, fetchMore } = useQuery(SEARCH_JOBS_QUERY, {
    variables,
    skip: hasSeedJobs,
  });

  if (loading && !hasSeedJobs) return <Loader />;
  if (error && !hasSeedJobs) return <p>Error: {error.message}</p>;

  const seedJobs = hasSeedJobs ? props.jobs : data?.searchJobs || [];
  const sourceCount = seedJobs.length + appended.length;
  let jobs = [...seedJobs, ...appended];
  if (props.jobId) {
    jobs = jobs.filter((job) => job.id !== props.jobId);
  }

  const endReached =
    moreExhausted || sourceCount === 0 || sourceCount % take !== 0;

  const isMedical =
    (props.q && props.q.toLowerCase().includes("medical")) ||
    (props.category && props.category.toLowerCase().includes("medical"));

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      if (hasSeedJobs) {
        const result = await client.query({
          query: SEARCH_JOBS_QUERY,
          variables: { ...variables, skip: sourceCount },
          fetchPolicy: "network-only",
        });
        const more = result.data?.searchJobs || [];
        if (more.length < take) setMoreExhausted(true);
        setAppended((prev) => [...prev, ...more]);
        return;
      }

      await fetchMore({
        variables: {
          ...variables,
          skip: sourceCount,
          take,
        },
        updateQuery(prev, { fetchMoreResult }) {
          if (!fetchMoreResult) return prev;
          const more = fetchMoreResult.searchJobs || [];
          if (more.length < take) setMoreExhausted(true);
          return {
            ...prev,
            searchJobs: [...(prev.searchJobs || []), ...more],
          };
        },
      });
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <StyledJobsCards>
      <JobList
        jobs={jobs}
        showJobType={jobsSettings.showJobType}
        showPayRate={jobsSettings.showPayRate}
        compact={props.compact}
      />
      {!endReached && jobs.length > 0 && (
        <Button
          disabled={loading || loadingMore}
          fullWidth
          color={isMedical ? "5" : ""}
          onClick={loadMore}
        >
          <Translator>{appText.actions.viewMore}</Translator>
        </Button>
      )}
      {endReached && (
        <p className="BottomMessage">{appText.messages.thatsAll} 😊</p>
      )}
    </StyledJobsCards>
  );
};

export default JobsCards;
